import React from 'react';
import { RefreshCw, Timer } from 'lucide-react';

/** Polling interval when auto-refresh is enabled (run logs, timeline, category detail). */
export const WORKFLOW_LOG_AUTO_REFRESH_MS = 10_000;

export type WorkflowLogModalToolbarProps = {
  onManualRefresh: () => void;
  isRefreshing: boolean;
  autoRefreshEnabled: boolean;
  onAutoRefreshEnabledChange: (value: boolean) => void;
  disabled?: boolean;
  /** Portal theme: `true` when ThemeContext is `white`. */
  isLight: boolean;
};

/**
 * Compact refresh + auto-refresh control (modal footers). Styles follow portal black / white theme.
 */
export function WorkflowLogModalToolbar({
  onManualRefresh,
  isRefreshing,
  autoRefreshEnabled,
  onAutoRefreshEnabledChange,
  disabled = false,
  isLight,
}: WorkflowLogModalToolbarProps) {
  const wrap = isLight
    ? 'border-slate-200 bg-white shadow-sm ring-1 ring-slate-900/5'
    : 'border-slate-600/80 bg-slate-800/60 shadow-sm';

  const refreshBtn = isLight
    ? 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
    : 'text-slate-200 hover:bg-slate-700/90 hover:text-white';

  const divider = isLight ? 'bg-slate-200' : 'bg-slate-600';

  const autoBtn = isLight
    ? 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
    : 'text-slate-300 hover:bg-slate-700/80 hover:text-slate-100';

  const timerIcon = isLight ? 'text-slate-500' : 'text-slate-400';

  const trackOff = isLight ? 'bg-slate-300' : 'bg-slate-600';

  return (
    <div className={`inline-flex items-center rounded-lg border p-0.5 ${wrap}`}>
      <button
        type="button"
        onClick={onManualRefresh}
        disabled={disabled || isRefreshing}
        className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition disabled:pointer-events-none disabled:opacity-40 ${refreshBtn}`}
        title="Reload now"
      >
        <RefreshCw className={`h-3.5 w-3.5 shrink-0 ${isRefreshing ? 'animate-spin' : ''}`} aria-hidden />
        Refresh
      </button>
      <span className={`mx-0.5 hidden h-5 w-px sm:block ${divider}`} aria-hidden />
      <button
        type="button"
        role="switch"
        aria-checked={autoRefreshEnabled}
        onClick={() => onAutoRefreshEnabledChange(!autoRefreshEnabled)}
        disabled={disabled}
        className={`inline-flex min-h-[2.25rem] items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs font-medium transition disabled:opacity-40 sm:px-3 ${autoBtn}`}
        title={`Poll every ${WORKFLOW_LOG_AUTO_REFRESH_MS / 1000} seconds while enabled`}
      >
        <Timer className={`h-3.5 w-3.5 shrink-0 ${timerIcon}`} aria-hidden />
        <span className="hidden whitespace-nowrap sm:inline">Auto</span>
        <span className="inline sm:hidden">A</span>
        <span
          className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
            autoRefreshEnabled ? 'bg-blue-600' : trackOff
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ease-out ${
              autoRefreshEnabled ? 'translate-x-4' : 'translate-x-0'
            }`}
          />
        </span>
      </button>
    </div>
  );
}
